import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { demoSentence } from '../data'

// A working miniature of the app's reader, driven by the same two settings
// the app exposes and the same tap logic as ReaderViewModel.handleTap:
// selecting the thing you already had selected clears it, and switching mode
// clears the other kind of selection.
//
// Punctuation is not interactive here. The app tokenises it, but a full stop
// has no pinyin or gloss worth opening a popup for.
export default function MiniReader() {
  const [tapMode, setTapMode] = useState('word') // 'word' | 'sentence'
  const [displayMode, setDisplayMode] = useState('pinyin') // 'pinyin' | 'hanzi'
  const [selectedWord, setSelectedWord] = useState(null)
  const [sentenceOpen, setSentenceOpen] = useState(false)

  const showPinyin = displayMode === 'pinyin'

  function handleTap(index) {
    if (tapMode === 'word') {
      setSentenceOpen(false)
      setSelectedWord((current) => (current === index ? null : index))
    } else {
      setSelectedWord(null)
      setSentenceOpen((open) => !open)
    }
  }

  function changeTapMode(mode) {
    setTapMode(mode)
    setSelectedWord(null)
    setSentenceOpen(false)
  }

  const token = selectedWord === null ? null : demoSentence.tokens[selectedWord]

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
        <Toggle
          label="Translation Mode"
          value={tapMode}
          onChange={changeTapMode}
          options={[
            ['word', 'Word'],
            ['sentence', 'Sentence'],
          ]}
        />
        <Toggle
          label="Writing System"
          value={displayMode}
          onChange={setDisplayMode}
          options={[
            ['pinyin', 'Hanzi + Pinyin'],
            ['hanzi', 'Hanzi only'],
          ]}
        />
      </div>

      {/* Fixed height so toggling pinyin reflows inside the frame without
          shoving the rest of the page around. */}
      <div className="w-full max-w-[22rem] rounded-[2.25rem] border-[6px] border-handu-ink/85 bg-handu-bg shadow-[0_18px_40px_rgba(38,24,22,0.18)]">
        <div className="flex h-[30rem] flex-col overflow-hidden rounded-[1.75rem]">
          <div className="flex items-center gap-2 border-b border-handu-outline/60 px-4 py-3">
            <span aria-hidden="true" className="text-lg text-handu-primary">
              ‹
            </span>
            <p className="flex-1 truncate text-sm font-semibold">
              David&apos;s Daily Life
            </p>
            <span
              aria-hidden="true"
              className="text-base text-handu-primary"
              title="Reading options"
            >
              ⚙
            </span>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-5">
            <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-handu-soft/70">
              {tapMode === 'word'
                ? 'Tap a word'
                : 'Tap anywhere in the sentence'}
            </p>

            <div
              className={`flex flex-wrap items-end gap-x-1 gap-y-2 rounded-lg px-1 py-1 transition-colors ${
                sentenceOpen ? 'bg-handu-goldbg' : ''
              }`}
            >
              {demoSentence.tokens.map((t, i) => {
                const isSelected = selectedWord === i
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleTap(i)}
                    aria-pressed={isSelected}
                    aria-label={`${t.hanzi} — ${t.pinyin}, ${t.translation}`}
                    className={`flex flex-col items-center rounded-md px-1 py-0.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-handu-primary/60 ${
                      isSelected ? 'bg-handu-mutedbg' : 'hover:bg-handu-mutedbg/60'
                    }`}
                  >
                    {showPinyin && (
                      <span className="text-[11px] leading-tight text-handu-soft/80">
                        {t.pinyin}
                      </span>
                    )}
                    <span className="hanzi text-2xl leading-tight">
                      {t.hanzi}
                      {t.trailing && (
                        <span className="text-handu-ink">{t.trailing}</span>
                      )}
                    </span>
                  </button>
                )
              })}
            </div>

            <AnimatePresence mode="wait">
              {token && (
                <Popup key={`word-${selectedWord}`}>
                  <p className="hanzi text-xl font-medium">{token.hanzi}</p>
                  <p className="mt-0.5 text-sm text-handu-soft">{token.pinyin}</p>
                  <p className="mt-2 text-[15px]">{token.translation}</p>
                </Popup>
              )}

              {sentenceOpen && (
                <Popup key="sentence">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-handu-soft/70">
                    Sentence
                  </p>
                  <p className="mt-1.5 text-[15px]">{demoSentence.translation}</p>
                  <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-handu-soft/70">
                    Words in sentence
                  </p>
                  <ul className="mt-1.5 space-y-1">
                    {demoSentence.tokens.map((t) => (
                      <li key={t.hanzi} className="flex gap-2 text-[13px]">
                        <span className="hanzi shrink-0 font-medium">{t.hanzi}</span>
                        <span className="shrink-0 text-handu-soft/80">
                          {t.pinyin}
                        </span>
                        <span className="text-handu-soft">{t.translation}</span>
                      </li>
                    ))}
                  </ul>
                </Popup>
              )}
            </AnimatePresence>
          </div>

          <div className="border-t border-handu-outline/60 px-4 py-3">
            <div className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-handu-gold bg-handu-container py-2.5 text-sm font-semibold text-white">
              Mark as Read
            </div>
          </div>
        </div>
      </div>

      <p className="max-w-md text-center text-sm text-handu-soft">
        This is the real thing, running here in the page — the same two
        settings, the same tap behaviour.
      </p>
    </div>
  )
}

function Popup({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
      className="mt-5 rounded-xl border border-handu-outline/70 bg-white px-4 py-3 shadow-[0_2px_8px_rgba(38,24,22,0.08)]"
    >
      {children}
    </motion.div>
  )
}

function Toggle({ label, value, onChange, options }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-handu-soft/70">
        {label}
      </span>
      <div
        role="group"
        aria-label={label}
        className="flex rounded-full border border-handu-outline bg-white/70 p-1"
      >
        {options.map(([key, text]) => (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            aria-pressed={value === key}
            className={`rounded-full px-3.5 py-1.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-handu-primary/60 ${
              value === key
                ? 'bg-handu-container font-semibold text-white'
                : 'text-handu-soft hover:text-handu-primary'
            }`}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  )
}
